<template>
  <div class="trainer-page">
    <v-card class="trainer-card">
      <div class="trainer-header">
        <div class="trainer-header__copy">
          <div class="trainer-title">Trainers</div>
          <div class="trainer-subtitle">Manage trainer profiles and course assignments.</div>
        </div>
        <v-text-field
            v-model="search"
            class="trainer-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search by name, phone or email"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateTrainer"
            class="trainer-add-button"
            depressed
            @click="addTrainer()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Trainer</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="trainers.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="trainers.count"
          class="trainer-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          {{ item.fullName ? item.fullName : "-" }}
        </template>
        <template v-slot:[`item.gender`]="{ item }">
          {{ item.gender ? item.gender : "-" }}
        </template>
        <template v-slot:[`item.email`]="{ item }">
          {{ item.email ? item.email : "-" }}
        </template>
        <template v-slot:[`item.profession.name`]="{ item }">
          {{ item.profession ? item.profession.name : "-" }}
        </template>
        <template v-slot:[`item.otherProfessionName`]="{ item }">
          {{ item.otherProfessionName ? item.otherProfessionName : "-" }}
        </template>
        <template v-slot:[`item.verificationStatus`]="{ item }">
          <v-chip
              :color="verificationStatusColor(item.verificationStatus)"
              dark
              small
          >
            {{ verificationStatusLabel(item.verificationStatus) }}
          </v-chip>
        </template>
        <template v-if="canViewTrainer" v-slot:[`item.actions`]="{ item }">
          <div class="trainer-action-group">
            <v-btn
                v-if="$can('trainer', 'verify') && (item.canVerifyTrainer && item.verificationStatus !== 'VERIFIED')"
                small
                text
                color="primary"
                :loading="verifyingTrainerId === item.id"
                :disabled="verifyingTrainerId !== null"
                @click="reviewTrainer(item, 'verify')"
            >Verify Trainer</v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                    class="trainer-action-icon"
                    @click="viewTrainer(item)"
                    v-on="on"
                >mdi-eye
                </v-icon
                >
              </template>
              <span>View Trainer</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canEditTrainer && item.canEditTrainer !== false" v-slot:activator="{ on }">
                <v-icon
                    class="trainer-action-icon"
                    @click="editTrainer(item)"
                    v-on="on"
                >mdi-circle-edit-outline
                </v-icon
                >
              </template>
              <span>Edit Trainer</span>
            </v-tooltip>
            <v-tooltip v-if="canReviewTrainer(item) && item.verificationStatus !== 'REJECTED'" bottom>
              <template v-slot:activator="{ on }">
                <v-icon
 v-if="$can('trainer', 'reject')"                    class="trainer-action-icon"
                    color="error"
                    :disabled="verifyingTrainerId !== null"
                    @click="reviewTrainer(item, 'reject')"
                    v-on="on"
                >mdi-account-cancel-outline
                </v-icon>
              </template>
              <span>Reject Trainer</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="60%">
        <v-card class="trainer-dialog-card">
          <v-card-title
              class="trainer-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="trainer-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddTrainer
              v-if="dialog"
              :trainer="trainer"
              @close="closeDialog()"
              @registerOrUpdateTrainer="registerOrUpdateTrainer"
          ></HrdAddTrainer>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="trainer-dialog-card">
          <v-card-title
              class="trainer-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="trainer-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdTrainerDetail
              v-if="detailDialog"
              :trainer="trainer"
          ></HrdTrainerDetail>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>


<script>
import HrdAddTrainer from "./hrd-add-trainer";
import HrdTrainerDetail from "./hrd-trainer-detail";
import {TrainerService} from "./trainers.service";
import {getUser} from "@/utils/helper";

export default {
  components: {
    HrdAddTrainer,
    HrdTrainerDetail,
  },

  data() {
    return {
      dialog: false,
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: "",
      searchDebounce: null,
      trainers: [],
      headers: [
        {text: "Trainer Full Name", value: "fullName"},
        {text: "Gender", value: "gender"},
        {text: "Email", value: "email"},
        {text: "Profession", value: "profession.name"},
        {text: "Other Profession Name", value: "otherProfessionName"},
        {text: "Verification", value: "verificationStatus", sortable: false},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      loading: false,
      trainer: null,
      verifyingTrainerId: null,
    };
  },
  async beforeMount() {
    this.fetchTrainerList();
  },
  watch: {
    options: {
      handler() {
        this.fetchTrainerList();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchTrainerList();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchTrainerList();
      }, 400);
    },
  },
  computed: {
    canCreateTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canCreate
      );
    },
    canEditTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canUpdate
      );
    },
    canViewTrainer() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Trainer" && permission.canRead
      );
    },
  },
  methods: {
    verificationLabel(status) {
      return {VERIFIED: "Verified", REJECTED: "Rejected"}[status] || "Pending verification";
    },
    async verifyTrainer(item) {
      if (!item.canVerifyTrainer || this.verifyingTrainerId !== null) return;
      this.verifyingTrainerId = item.id;
      try {
        const res = await TrainerService.verifyTrainer(item.id, {action: "verify", comment: ""});
        if (res.data?.status !== 200) {
          this.$toast.error(res.data?.message || "Unable to verify trainer.");
          return;
        }
        Object.assign(item, res.data.data);
        this.$toast.success(res.data.message);
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Unable to verify trainer.");
      } finally {
        this.verifyingTrainerId = null;
      }
    },
    async fetchTrainerList() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      const res = await TrainerService.getTrainersWP(
          page,
          itemsPerPage,
          this.search
      );
      if (res.status === 200) {
        this.loading = false;
        this.trainers = res.data;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching trainers, Please try Again!"
        );
      }
    },
    viewTrainer(item) {
      this.trainer = item;
      this.detailDialog = true;
      this.title = "Trainer Detail";
    },
    addTrainer() {
      this.trainer = null;
      this.dialog = true;
      this.title = "Add Trainer";
    },
    editTrainer(item) {
      if (item?.canEditTrainer === false) {
        this.$toast.error("You can only edit trainers registered by your CPD provider.");
        return;
      }
      this.trainer = item;
      this.dialog = true;
      this.title = "Edit Trainer";
    },
    verificationStatusLabel(status) {
      const value = String(status || "PENDING_VERIFICATION").toUpperCase();
      if (value === "VERIFIED") return "Verified";
      if (value === "REJECTED") return "Rejected";
      return "Pending Verification";
    },
    verificationStatusColor(status) {
      const value = String(status || "PENDING_VERIFICATION").toUpperCase();
      if (value === "VERIFIED") return "success";
      if (value === "REJECTED") return "error";
      return "warning";
    },
    canReviewTrainer(item) {
      return item?.canVerifyTrainer === true;
    },
    async reviewTrainer(item, action) {
      if (!this.canReviewTrainer(item) || this.verifyingTrainerId !== null) return;
      let comment = null;
      if (action === "reject") {
        comment = window.prompt("Enter the reason for rejecting this trainer:");
        if (comment === null) return;
      }
      this.verifyingTrainerId = item.id;
      try {
        const response = await TrainerService.verifyTrainer(item.id, {action, comment});
        const body = response?.data || {};
        if (body.status !== 200) {
          this.$toast.error(body.message || "Unable to review trainer.");
          return;
        }
        Object.assign(item, body.data);
        this.$toast.success(body.message);
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Unable to review trainer.");
      } finally {
        this.verifyingTrainerId = null;
      }
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.fetchTrainerList();
    },
    async registerOrUpdateTrainer(trainer) {
      let res = null;

      if (trainer.editMode) {
        res = await TrainerService.update(trainer.id, trainer);

        if (res.status === 201 || res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.closeDialog();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to update trainer, please try again");
        }
      } else {
        res = await TrainerService.create(trainer);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.closeDialog();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to create trainer, please try again");
        }
      }
    },
  },
};
</script>
<style scoped>
.trainer-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.trainer-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.trainer-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.trainer-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.trainer-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.trainer-subtitle {
  font-size: 14px;
  color: #475569;
}

.trainer-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.trainer-table {
  border-radius: 0 0 12px 12px;
}

.trainer-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.trainer-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.trainer-dialog__close {
  color: #ffffff !important;
}

.trainer-dialog__close .v-icon {
  color: #ffffff !important;
}

.trainer-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.trainer-action-icon {
  color: #1976d2 !important;
}

.trainer-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.trainer-action-group .v-tooltip {
  display: inline-flex;
}

@media (max-width: 700px) {
  .trainer-page {
    padding: 12px;
  }

  .trainer-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .trainer-search {
    max-width: 100%;
  }

  .trainer-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
